import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Package,
  AlertTriangle,
  TrendingUp
} from 'lucide-react'
// import { stockAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function Stock() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [showLowStock, setShowLowStock] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingStock, setEditingStock] = useState(null)

  const queryClient = useQueryClient()

  // Fetch stocks
  const { data: stocksData, isLoading } = useQuery(
    ['stocks', { page: currentPage, search: searchTerm, category: selectedCategory, lowStock: showLowStock }],
    () => stockAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      category: selectedCategory,
      lowStock: showLowStock,
    })
  )

  // Create stock mutation
  const createStockMutation = useMutation(stockAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('stocks')
      setShowModal(false)
      toast.success('Stock item created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create stock item')
    },
  })

  // Update stock mutation
  const updateStockMutation = useMutation(
    ({ id, data }) => stockAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('stocks')
        setShowModal(false)
        setEditingStock(null)
        toast.success('Stock item updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update stock item')
      },
    }
  )

  // Delete stock mutation
  const deleteStockMutation = useMutation(stockAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('stocks')
      toast.success('Stock item deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete stock item')
    },
  })

  // Toggle status mutation
  const toggleStatusMutation = useMutation(stockAPI.toggleStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries('stocks')
      toast.success('Stock status updated successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update stock status')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingStock) {
      updateStockMutation.mutate({ id: editingStock.id, data })
    } else {
      createStockMutation.mutate(data)
    }
  }

  const handleEdit = (stock) => {
    setEditingStock(stock)
    reset({
      itemName: stock.itemName,
      category: stock.category,
      unit: stock.unit,
      totalQuantity: stock.totalQuantity,
      availableQuantity: stock.availableQuantity,
      damageQuantity: stock.damageQuantity,
      usedQuantity: stock.usedQuantity,
      lowStockAlert: stock.lowStockAlert,
      pricePerQuantity: stock.pricePerQuantity,
      vendorName: stock.vendorName,
      vendorContact: stock.vendorContact,
      vendorAddress: stock.vendorAddress,
      description: stock.description,
    })
    setShowModal(true)
  }

  const handleDelete = (stock) => {
    if (window.confirm('Are you sure you want to delete this stock item?')) {
      deleteStockMutation.mutate(stock.id)
    }
  }

  const handleToggleStatus = (stock) => {
    toggleStatusMutation.mutate(stock.id)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingStock(null)
    reset()
  }

  const stocks = stocksData?.data?.stocks || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Stock Management</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage inventory items and stock levels
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Stock Item
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search stock items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="label">Category</label>
              <input
                type="text"
                placeholder="Filter by category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showLowStock}
                  onChange={(e) => setShowLowStock(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <span className="ml-2 text-sm text-secondary-700">Show low stock only</span>
              </label>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('')
                  setShowLowStock(false)
                }}
                className="btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Item</th>
                    <th className="table-head">Category</th>
                    <th className="table-head">Quantity</th>
                    <th className="table-head">Price</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {stocks.map((stock) => (
                    <tr key={stock.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <Package className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {stock.itemName}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {stock.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-secondary">
                          {stock.category || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          <div>Available: {stock.availableQuantity}</div>
                          <div>Total: {stock.totalQuantity}</div>
                          {stock.availableQuantity <= stock.lowStockAlert && (
                            <div className="flex items-center text-red-600">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Low Stock
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          ₹{stock.pricePerQuantity || 0}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${stock.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {stock.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(stock)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(stock)}
                            className={stock.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}
                          >
                            <TrendingUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(stock)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Stock Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingStock ? 'Edit Stock Item' : 'Add New Stock Item'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Item Name</label>
                    <input
                      {...register('itemName', { required: 'Item name is required' })}
                      className="input"
                      placeholder="Enter item name"
                    />
                    {errors.itemName && (
                      <p className="form-error">{errors.itemName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <input
                      {...register('category')}
                      className="input"
                      placeholder="Enter category"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Unit</label>
                    <select
                      {...register('unit')}
                      className="input"
                    >
                      <option value="pieces">Pieces</option>
                      <option value="meters">Meters</option>
                      <option value="yards">Yards</option>
                      <option value="kg">Kilograms</option>
                      <option value="grams">Grams</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Total Quantity</label>
                    <input
                      {...register('totalQuantity', { required: 'Total quantity is required' })}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter total quantity"
                    />
                    {errors.totalQuantity && (
                      <p className="form-error">{errors.totalQuantity.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Available Quantity</label>
                    <input
                      {...register('availableQuantity', { required: 'Available quantity is required' })}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter available quantity"
                    />
                    {errors.availableQuantity && (
                      <p className="form-error">{errors.availableQuantity.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Damage Quantity</label>
                    <input
                      {...register('damageQuantity')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter damage quantity"
                    />
                  </div>
                  <div>
                    <label className="label">Used Quantity</label>
                    <input
                      {...register('usedQuantity')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter used quantity"
                    />
                  </div>
                  <div>
                    <label className="label">Low Stock Alert</label>
                    <input
                      {...register('lowStockAlert')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter low stock alert level"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Price Per Quantity</label>
                    <input
                      {...register('pricePerQuantity')}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter price per quantity"
                    />
                  </div>
                  <div>
                    <label className="label">Vendor Name</label>
                    <input
                      {...register('vendorName')}
                      className="input"
                      placeholder="Enter vendor name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Vendor Contact</label>
                    <input
                      {...register('vendorContact')}
                      className="input"
                      placeholder="Enter vendor contact"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Vendor Address</label>
                  <textarea
                    {...register('vendorAddress')}
                    className="input"
                    rows={2}
                    placeholder="Enter vendor address"
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    {...register('description')}
                    className="input"
                    rows={3}
                    placeholder="Enter item description"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createStockMutation.isLoading || updateStockMutation.isLoading}
                    className="btn-primary"
                  >
                    {createStockMutation.isLoading || updateStockMutation.isLoading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : null}
                    {editingStock ? 'Update Stock Item' : 'Create Stock Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Stock
